import { dayjs } from '@karve.it/core';
import { MemoizedSelector, createSelector } from '@ngrx/store';

import { QuillTemplateLocation } from '../../../generated/graphql.generated';
import { JOB_ROLE_MAP } from '../../global.constants';
import { selectCustomer, selectCustomerName, selectJob, selectJobEvents, selectJobLocations, selectJobSource } from '../../job/job-tool.reducer';
import { selectDispatchTemplateVariableData } from '../../schedules/dispatch/store/dispatch.selectors';
import { formatPhoneNumber } from '../phone.pipe';
import { convertSecondsToTimeUnit } from '../seconds-to-time-unit.pipe';

/**
 * Builds the variable data for the job-page editors by reading the `jobTool`
 * store. This is the data half of the contract whose declarations live in
 * `TEMPLATE_VARIABLES` under the same job locations.
 */
export const selectJobTemplateVariableData = createSelector(
  selectJob,
  selectCustomer,
  selectCustomerName,
  selectJobEvents,
  selectJobLocations,
  selectJobSource,
  (job, customer, customerName, events, locations, jobSource) => {
    const firstRevenueGeneratingEvent = events?.find((e) => e.event.total);
    const firstLocation = firstRevenueGeneratingEvent?.locations?.[0]?.type === 'basestart'
      ? firstRevenueGeneratingEvent?.locations?.[1]
      : firstRevenueGeneratingEvent?.locations?.[0];

    const usersWithName = (job?.users || [])
      .map((user) => {
        const name = [user.user.givenName, user.user.familyName].filter(Boolean).join(' ');
        return {
          role: user.role,
          user: { ...user.user, name },
        };
      });

    const salesAgent = usersWithName.find((user) => user.role === JOB_ROLE_MAP.salesAgentRole)?.user;
    const estimator = usersWithName.find((user) => user.role === JOB_ROLE_MAP.estimatorRole)?.user;

    return {
      job,
      misc: {
        salesAgent,
        estimator,
        moveDate: job?.timeline ? dayjs(job.timeline).unix() : firstRevenueGeneratingEvent?.event?.start,
        travelTimeToFirstLocation: firstLocation?.totalTravelTimeToNextLocation
          ? convertSecondsToTimeUnit(firstLocation.totalTravelTimeToNextLocation).trim()
          : null,
        customer: {
          ...customer,
          name: customerName,
          phone: formatPhoneNumber(customer?.phone, true),
        },
        locations,
        howDidTheyHearAboutUs: jobSource,
      },
    };
  },
);

/**
 * Maps a `QuillTemplateLocation` to the selector that produces the variable
 * data for that location. This is the data-side parallel of `TEMPLATE_VARIABLES`
 * (which maps the same locations to variable declarations); sharing the key
 * makes a declarations/data mismatch structurally impossible.
 */
export const TEMPLATE_VARIABLE_DATA: Partial<Record<QuillTemplateLocation, MemoizedSelector<object, any>>> = {
  [QuillTemplateLocation.GenericJobComment]: selectJobTemplateVariableData,
  [QuillTemplateLocation.JobCommentSendEmailToCustomer]: selectJobTemplateVariableData,
  [QuillTemplateLocation.JobCommentSendSmsToCustomer]: selectJobTemplateVariableData,
  [QuillTemplateLocation.JobCommentNotifyJobWatchers]: selectJobTemplateVariableData,
  [QuillTemplateLocation.JobCommentAskBee]: selectJobTemplateVariableData,
  [QuillTemplateLocation.JobCommentScheduleCommunication]: selectJobTemplateVariableData,

  [QuillTemplateLocation.JobSummary]: selectJobTemplateVariableData,
  [QuillTemplateLocation.JobCrewSummary]: selectJobTemplateVariableData,
  [QuillTemplateLocation.JobAdminSummary]: selectJobTemplateVariableData,
  [QuillTemplateLocation.JobCustomerSummary]: selectJobTemplateVariableData,

  [QuillTemplateLocation.EstimateSendEmail]: selectJobTemplateVariableData,
  [QuillTemplateLocation.EstimateSendSms]: selectJobTemplateVariableData,
  [QuillTemplateLocation.ContractSendEmail]: selectJobTemplateVariableData,
  [QuillTemplateLocation.ContractSendSms]: selectJobTemplateVariableData,
  [QuillTemplateLocation.InvoiceSendEmail]: selectJobTemplateVariableData,
  [QuillTemplateLocation.InvoiceSendSms]: selectJobTemplateVariableData,
  [QuillTemplateLocation.ReceiptSendEmail]: selectJobTemplateVariableData,
  [QuillTemplateLocation.ReceiptSendSms]: selectJobTemplateVariableData,
  [QuillTemplateLocation.BillOfLading]: selectJobTemplateVariableData,

  [QuillTemplateLocation.DispatchSchedule]: selectDispatchTemplateVariableData,
};
